import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { ConsentPreferredTime, ConsentPreferredTimeLabels } from '../../../../@types/ConsentPreferredTime';
import type { Owner } from '../../../../@types/Owner';
import { useReferentSendAvailability } from '../../../../hooks/useReferentSendAvailability';
import type { RootState } from '../../../../store/store';
import { showToast } from '../../../common/Notification';
import Popup from '../../../common/Popup';

interface CallbackRequestFormProps {
  owner: Owner;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  recallAt: string;
  preferredTime: ConsentPreferredTime;
  optin: boolean;
}

// Helper function to format date to yyyy-MM-dd format
const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

const CallbackRequestForm: React.FC<CallbackRequestFormProps> = ({
  owner,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);
  const { getOwnerCompany } = useReferentSendAvailability(company?.id);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    recallAt: '',
    preferredTime: ConsentPreferredTime.NO_PREFERENCE,
    optin: false,
  });

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const selectedDate = new Date(value);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    if (selectedDate >= now || value === '') {
      setFormData(prev => ({ ...prev, recallAt: value }));
    } else {
      showToast('error', 'Veuillez sélectionner une date future pour le rappel.');
    }
  };

  const handlePreferredTimeChange = (time: ConsentPreferredTime) => {
    setFormData(prev => ({ ...prev, preferredTime: time }));
  };

  const handleOptinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, optin: e.target.checked }));
  };

  const handleSubmit = async() => {
    if (!formData.optin) {
      showToast('error', 'Veuillez accepter les conditions pour continuer.');
      return;
    }

    if (!owner.id) {
      showToast('error', 'Identifiant propriétaire manquant.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`/api/owners/${owner.id}/consents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          recallAt: formData.recallAt || null,
          preferredTime: formData.preferredTime,
          optin: formData.optin,
          phone: owner.phone || '',
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de l\'enregistrement');
      }

      showToast('success', 'Demande de rappel enregistrée avec succès !');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating callback request:', error);
      showToast('error', error instanceof Error ? error.message : 'Une erreur est survenue lors de l\'enregistrement.');
    } finally {
      setLoading(false);
    }
  };

  const companyName = getOwnerCompany(owner)?.name || 'votre syndic';

  return (
    <Popup
      isOpen={isOpen}
      onClose={onClose}
      title="Enregistrer une prise de rendez-vous"
      width="500px"
      maxWidth="90vw"
      height="auto"
      maxHeight="90vh"
      onConfirm={handleSubmit}
      confirmLabel="Enregistrer"
      onCancel={() => {}}
      cancelLabel="Annuler"
      loading={loading}
    >
      <div className="space-y-6">
        <p className="text-sm text-gray-600">
          Enregistrer une demande de rappel pour <strong>{owner.firstname} {owner.lastname}</strong>
          {owner.phone && <> au <strong>{owner.phone}</strong></>}.
        </p>

        {/* Date de rappel */}
        <div>
          <label htmlFor="recallAt" className="mb-1 block text-sm font-medium text-gray-700">
            Date de rappel souhaitée
          </label>
          <input
            id="recallAt"
            name="recallAt"
            type="date"
            min={formatDateForInput(new Date())}
            value={formData.recallAt}
            onChange={handleDateChange}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-transparent focus:ring-2 focus:ring-rgpd-blue-light focus:outline-none"
          />
        </div>

        {/* Moment préféré */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Moment de la journée préféré pour le rappel
          </label>
          <div className="flex flex-col space-y-2">
            <div className="flex flex-row space-x-2">
              <button
                type="button"
                onClick={() => handlePreferredTimeChange(ConsentPreferredTime.MORNING)}
                className={`flex-1 rounded-md border px-4 py-2 text-sm font-medium shadow-sm transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-offset-2 focus:outline-none ${
                  formData.preferredTime === ConsentPreferredTime.MORNING
                    ? 'border-transparent bg-rgpd-blue-light text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {ConsentPreferredTimeLabels[ConsentPreferredTime.MORNING]}
              </button>
              <button
                type="button"
                onClick={() => handlePreferredTimeChange(ConsentPreferredTime.EVENING)}
                className={`flex-1 rounded-md border px-4 py-2 text-sm font-medium shadow-sm transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-offset-2 focus:outline-none ${
                  formData.preferredTime === ConsentPreferredTime.EVENING
                    ? 'border-transparent bg-rgpd-blue-light text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {ConsentPreferredTimeLabels[ConsentPreferredTime.EVENING]}
              </button>
            </div>
            <button
              type="button"
              onClick={() => handlePreferredTimeChange(ConsentPreferredTime.NO_PREFERENCE)}
              className={`rounded-md border px-4 py-2 text-sm font-medium shadow-sm transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-offset-2 focus:outline-none ${
                formData.preferredTime === ConsentPreferredTime.NO_PREFERENCE
                  ? 'border-transparent bg-rgpd-blue-light text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {ConsentPreferredTimeLabels[ConsentPreferredTime.NO_PREFERENCE]}
            </button>
          </div>
        </div>

        {/* Optin checkbox */}
        <div className="flex items-start">
          <input
            id="optin"
            name="optin"
            type="checkbox"
            checked={formData.optin}
            onChange={handleOptinChange}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-rgpd-blue-light focus:ring-rgpd-blue-light"
          />
          <label htmlFor="optin" className="ml-2 block text-sm text-gray-700">
            Le propriétaire accepte d'être contacté(e) par {companyName} pour être informé(e) sur les documents nécessaires à son projet dans le cadre de son activité de syndic
          </label>
        </div>

        {!formData.optin && (
          <p className="text-xs text-amber-600">
            Le consentement doit être accepté pour enregistrer la demande de rappel.
          </p>
        )}
      </div>
    </Popup>
  );
};

export default CallbackRequestForm;
